import { useQuery } from "@tanstack/react-query";
import type { NurseResponse } from "@vTypes/nurse-type";
import { useIsAdmin } from "./useAuth";
import httpClient from "@/config/axios";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";

/**
 * 간호사 목록과 프리셉터 그룹 정보를 함께 가져오는 커스텀 훅
 * @param selectedGroupId 현재 선택된 그룹 ID
 * @returns useQuery 결과 ({ nurses, preceptorGroups })
 */
export default function useNurses(
  selectedGroupId?: string | null,
  options?: { year?: number | null; month?: number | null; enabled?: boolean }
) {
  const isAdmin = useIsAdmin();
  // null을 ""로 변환하여 쿼리 키 일관성 유지 (Roster_create.tsx와 동일)
  const groupId = isAdmin ? (selectedGroupId ?? "") : "";
  const year = options?.year ?? null;
  const month = options?.month ?? null;

  return useQuery<NurseResponse[]>({
    queryKey: QUERY_KEYS.nurses.list(groupId, year, month),
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      return await httpClient.get<NurseResponse[]>("/nurses", {
        params: {
          ...(groupId && { group_id: groupId }),
          ...(year != null && { year }),
          ...(month != null && { month }),
        },
      });
    },
    staleTime: 5 * 60 * 1000, // 5분 캐싱
    retry: 1,
    retryDelay: 1000,
    select: (data: NurseResponse[]) => {
      const sortedNurses = [...data].sort(
        (a: { sequence: number }, b: { sequence: number }) =>
          a.sequence - b.sequence
      );
      return sortedNurses;
    },
  });
}
